import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockContainer from '../BlockContainer';

import css from './BlockFooter.module.css';

import { useIntl } from '../../../../util/reactIntl';

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * This returns a component that can render 'footerBlock' config.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.blockId id from the block config
 * @param {string} props.blockName name from the block config (not used)
 * @param {'footerBlock'} props.blockType blockType is set to 'footerBlock'
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.className add more styles in addition to components own css.root
 * @param {string?} props.textClassName add styles for the block's attached text field
 * @param {Object?} props.text content config for the block (can be markdown)
 * @param {Object} props.options extra options for the block component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents Custom fieldComponents
 * @returns {JSX.Element} component that renders block type: 'footerBlock'
 */
const BlockFooter = props => {
  const { blockId, className, rootClassName, textClassName, text, options } = props;
  const intl = useIntl();

  // Resolve Footer.xxx translation keys in markdown content
  const resolvedText = text?.content
    ? {
        ...text,
        content: text.content.replace(/Footer\.(\w+)/g, match => {
          return intl.messages[match] || match;
        }),
      }
    : text;

  const classes = classNames(rootClassName || css.root, className);
  const hasTextComponentFields = hasDataInFields([resolvedText], options);

  return (
    <BlockContainer id={blockId} className={classes}>
      {hasTextComponentFields ? (
        <nav
          className={classNames(textClassName, css.text)}
          aria-label={blockId || 'Footer navigation'}
        >
          <Field data={resolvedText} options={options} />
        </nav>
      ) : null}
    </BlockContainer>
  );
};

export default BlockFooter;
